
<template>
  <div class="settings--content">
    <loading-state v-if="showLoader" :message="processingMessage" />

    <form v-else @submit.prevent="launchEmbeddedSignup">
      <div class="embedded-signup-content">
        <p class="embedded-signup-title">{{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.TITLE') }}</p>
        <p>{{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.DESC') }}</p>
        <div class="button-container">
          <woot-submit-button
            :button-text="$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUBMIT_BUTTON')"
            :loading="isAuthenticating"
            :disabled="isAuthenticating"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import SettingsSection from 'dashboard/components/SettingsSection';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
  isValidBusinessData,
} from './whatsapp/utils';

export default {
  name: 'WhatsappEmbeddedSignup',
  components: {
    SettingsSection,
    LoadingState,
  },
  data() {
    return {
      fbSdkLoaded: false,
      isProcessing: false,
      processingMessage: '',
      authCodeReceived: false,
      authCode: null,
      businessData: null,
      isAuthenticating: false,
      handleSignupMessage: null,
    };
  },
  computed: {
    showLoader() {
      return this.isAuthenticating || this.isProcessing;
    },
  },
  mounted() {
    this.initialize();
  },
  beforeDestroy() {
    this.cleanupMessageListener();
  },
  methods: {
    // Error handling
    handleSignupError(data) {
      this.isProcessing = false;
      this.authCodeReceived = false;
      this.isAuthenticating = false;

      const errorMessage =
        data.error ||
        data.message ||
        this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
      bus.$emit('newToastMessage', errorMessage, 'error');
    },

    handleSignupCancellation() {
      this.isProcessing = false;
      this.authCodeReceived = false;
      this.isAuthenticating = false;
    },

    handleSignupSuccess(inboxData) {
      this.isProcessing = false;
      this.isAuthenticating = false;

      if (inboxData && inboxData.id) {
        bus.$emit('newToastMessage', this.$t('INBOX_MGMT.FINISH.MESSAGE'));
        this.$router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: inboxData.id,
          },
        });
      } else {
        bus.$emit('newToastMessage', this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUCCESS_FALLBACK'));
        this.$router.replace({
          name: 'settings_inbox_list',
        });
      }
    },

    // Signup flow
    async completeSignupFlow(businessDataParam) {
      if (!this.authCodeReceived || !this.authCode) {
        this.handleSignupError({
          error: this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_NOT_COMPLETED'),
        });
        return;
      }

      this.isProcessing = true;
      this.processingMessage = this.$t(
        'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
      );

      try {
        const params = {
          code: this.authCode,
          business_id: businessDataParam.business_id,
          waba_id: businessDataParam.waba_id,
          phone_number_id: businessDataParam?.phone_number_id || '',
        };

        const responseData = await this.$store.dispatch(
          'inboxes/createWhatsAppEmbeddedSignup',
          params
        );

        this.authCode = null;
        this.handleSignupSuccess(responseData);
      } catch (error) {
        const errorMessage =
          parseAPIErrorResponse(error) ||
          this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
        this.handleSignupError({ error: errorMessage });
      }
    },

    // Message handling
    async handleEmbeddedSignupData(data) {
      if (
        data.event === 'FINISH' ||
        data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
      ) {
        const businessDataLocal = data.data;

        if (isValidBusinessData(businessDataLocal)) {
          this.businessData = businessDataLocal;
          if (this.authCodeReceived && this.authCode) {
            await this.completeSignupFlow(businessDataLocal);
          } else {
            this.processingMessage = this.$t(
              'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_AUTH'
            );
          }
        } else {
          this.handleSignupError({
            error: this.$t(
              'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA'
            ),
          });
        }
      } else if (data.event === 'CANCEL') {
        this.handleSignupCancellation();
      } else if (data.event === 'error') {
        this.handleSignupError({
          error:
            data.error_message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR'),
          error_id: data.error_id,
          session_id: data.session_id,
        });
      }
    },

    async launchEmbeddedSignup() {
      try {
        this.isAuthenticating = true;
        this.processingMessage = this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_PROCESSING'
        );

        await setupFacebookSdk(
          window.chatwootConfig?.whatsappAppId,
          window.chatwootConfig?.whatsappApiVersion
        );
        this.fbSdkLoaded = true;

        const code = await initWhatsAppEmbeddedSignup(
          window.chatwootConfig?.whatsappConfigurationId
        );

        this.authCode = code;
        this.authCodeReceived = true;
        this.processingMessage = this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_BUSINESS_INFO'
        );

        if (this.businessData) {
          this.completeSignupFlow(this.businessData);
        }
      } catch (error) {
        if (error.message === 'Login cancelled') {
          this.isProcessing = false;
          this.isAuthenticating = false;
          bus.$emit('newToastMessage', this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
        } else {
          this.handleSignupError({
            error:
              error.message ||
              this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR'),
          });
        }
      }
    },

    // Lifecycle
    setupMessageListener() {
      window.addEventListener('message', this.handleSignupMessage);
    },

    cleanupMessageListener() {
      window.removeEventListener('message', this.handleSignupMessage);
    },

    initialize() {
      this.handleSignupMessage = createMessageHandler(this.handleEmbeddedSignupData);
      this.setupMessageListener();
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables';

.embedded-signup-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  padding: var(--space-normal) 0;

  p {
    white-space: pre-line;
  }
}

.embedded-signup-title {
  color: $color-converx;
  font-weight: $font-weight-medium;
  margin-bottom: 0;
}

.button-container {
  display: flex;
  justify-content: flex-start;
  margin-top: var(--space-normal);
}
</style>